// Driver fiscale Epson ePOS-Print (FP81 II RT e compatibili) — protocollo XML via HTTP
import { paymentMethodLabel } from "./paymentMethods";
import { lineTotal } from "@/lib/escpos";

// Mappa metodi VELTA → paymentType/index ePOS fiscale Epson (gestione a 3 cifre: tipo+indice)
// 0-00=Contanti · 1-00=Assegno · 2-01=Carta di credito (E) · 2-03=Altro pagamento elettronico (E)
// 3-01=Ticket · 5-00=Non riscosso · 6-00=Sconto a pagare (omaggio)
// ATTENZIONE: paymentType 2 con index 0 = "CREDITO" → classificato NON RISCOSSO dalla RT!
// Per i pagamenti elettronici (carta/bancomat) l'index valido è 1-10 (201=carta, 204=bancomat).
const PAYMENT_TYPE = {
  cash: { paymentType: 0, index: 0 },
  assegno: { paymentType: 1, index: 0 },
  card: { paymentType: 2, index: 1 },
  bonifico: { paymentType: 2, index: 3 },
  ticket: { paymentType: 3, index: 1 },
  non_riscosso: { paymentType: 5, index: 0 },
  omaggio: { paymentType: 6, index: 0 },
};

const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const n2 = (n) => Number(n || 0).toFixed(2);
const pad4 = (n) => String(Math.max(0, parseInt(n, 10) || 0)).padStart(4, "0");
const SOAP_OPEN = '<?xml version="1.0" encoding="utf-8"?><s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>';
const wrap = (inner) => SOAP_OPEN + inner + "</s:Body></s:Envelope>";

export const epsonEposDriver = {
  id: "epson_epos",
  label: "Epson ePOS (FP81 II RT)",
  // POST HTTP diretto dal browser verso fpmate.cgi (Content-Type text/xml, SOAPAction "").
  // NB: da pagina HTTPS il browser blocca le richieste HTTP verso la LAN (mixed content):
  // usare il tablet Android o aprire l'app in HTTP.
  transport: "http",
  port: 80, // fpmate CGI ascolta in HTTP sulla porta 80
  encoding: "UTF-8",

  // Ristampa ultimo scontrino fiscale come duplicato nativo sulla stampante RT;
  // per documenti non fiscali (o senza stampante RT) resta l'anteprima con Stampa.
  buildDuplicate: () => wrap('<printerCommand><printDuplicateReceipt operator="1" /></printerCommand>'),

  // Annullo documento commerciale: printRecMessage tipo 4 "VOID zzzz nnnn ggmmaaaa matricola"
  // (spec Epson: se il documento è nel DGFE la RT stampa e chiude l'annullo automaticamente)
  buildVoid: ({ zRep, docNum, dateDDMMYYYY, serial }) =>
    wrap(`<printerFiscalReceipt><printRecMessage operator="1" messageType="4" message="VOID ${pad4(zRep)} ${pad4(docNum)} ${dateDDMMYYYY} ${esc(serial)}" /></printerFiscalReceipt>`),

  // Reso parziale: scontrino di reso (quantità e prezzo positivi, il comando è printRecRefund).
  buildRefund: ({ order = {}, payments = [] }) => {
    let x = "<printerFiscalReceipt>";
    x += '<beginFiscalReceipt operator="1" />';
    (order.lines || []).forEach((l) => {
      const qty = Math.abs(Number(l.qty) || 1);
      const price = Math.abs(Number(l.price) || 0);
      x += `<printRecRefund operator="1" description="${esc(l.name)}" quantity="${qty}" unitPrice="${n2(price)}" department="1" justification="1" />`;
    });
    x += '<printRecSubtotal operator="1" option="1" />';
    const pay = payments.length ? payments : [{ method: order.payment_method || "cash", amount: Math.abs(Number(order.total) || 0) }];
    pay.forEach((p) => {
      const t = PAYMENT_TYPE[p.method] || PAYMENT_TYPE.cash;
      x += `<printRecTotal operator="1" description="${esc(paymentMethodLabel(p.method))}" payment="${n2(Math.abs(p.amount))}" paymentType="${t.paymentType}" index="${t.index}" justification="2" />`;
    });
    x += '<endFiscalReceipt operator="1" /></printerFiscalReceipt>';
    return wrap(x);
  },

  // Rapporti fiscali: Lettura X (non azzera) e Chiusura Z (azzera i contatori giornalieri)
  buildReport: (kind) =>
    wrap(kind === "z"
      ? '<printerFiscalReport><printZReport operator="1" timeout="12000" /></printerFiscalReport>'
      : '<printerFiscalReport><printXReport operator="1" /></printerFiscalReport>'),

  // Ristampa dal DGFE la copia conforme di un documento commerciale memorizzato
  buildContentReprint: ({ day, month, year, number }) =>
    wrap(`<printerCommand><printContentByNumbers operator="1" dataType="1" day="${day}" month="${month}" year="${year}" fromNumber="${number}" toNumber="${number}" /></printerCommand>`),

  // Estrae dall'addInfo di fpmate i riferimenti reali del documento appena emesso
  parseRt: (resp) => {
    const g = (tag) => (resp.match(new RegExp(`<${tag}>([^<]*)</${tag}>`)) || [])[1];
    const num = parseInt(g("fiscalReceiptNumber"), 10);
    if (!num) return null;
    const z = parseInt(g("zRepNumber"), 10);
    return {
      fiscal_number: num,
      ...(z ? { z_rep: z } : {}),
      ...(g("fiscalReceiptDate") ? { fiscal_date: g("fiscalReceiptDate") } : {}),
      ...(g("fiscalReceiptTime") ? { fiscal_time: g("fiscalReceiptTime") } : {}),
    };
  },

  buildReceipt: ({ order = {}, payments = [] }) => {
    let x = "<printerFiscalReceipt>";
    x += '<beginFiscalReceipt operator="1" />';
    (order.lines || []).forEach((l) => {
      x += `<printRecItem operator="1" description="${esc(l.name)}" quantity="${l.qty}" unitPrice="${n2(l.price)}" department="1" justification="1" />`;
      const gross = l.price * l.qty;
      const net = lineTotal(l);
      if (net < gross - 0.001) {
        x += `<printRecItemAdjustment operator="1" adjustmentType="0" description="Sconto" amount="${n2(gross - net)}" department="1" justification="2" />`;
      }
    });
    x += '<printRecSubtotal operator="1" option="1" />';
    const pct = Number(order.discount_pct || 0);
    const eurD = Number(order.discount_eur || 0);
    if (pct > 0 || eurD > 0) {
      const grossSum = (order.lines || []).reduce((s, l) => s + lineTotal(l), 0);
      const totalDisc = grossSum * (pct / 100) + Math.min(eurD, grossSum * (1 - pct / 100));
      if (totalDisc > 0.004) {
        x += `<printRecSubtotalAdjustment operator="1" adjustmentType="1" description="Sconto" amount="${n2(totalDisc)}" justification="2" />`;
      }
    }
    payments.forEach((p) => {
      const t = PAYMENT_TYPE[p.method] || PAYMENT_TYPE.cash;
      x += `<printRecTotal operator="1" description="${esc(paymentMethodLabel(p.method))}" payment="${n2(p.amount)}" paymentType="${t.paymentType}" index="${t.index}" justification="2" />`;
    });
    x += '<endFiscalReceipt operator="1" /></printerFiscalReceipt>';
    return wrap(x);
  },
};
